import React, { useState, useEffect, useRef } from 'react';
import { Card, Button, ProgressBar, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { runApiTests } from '../tests/apiTests';
import './Settings.scss'; // Wiederverwendung des Layouts

export default function AdminTests() {
  const { token } = useAuth();
  
  // Test-Status-Variablen
  const [isRunning, setIsRunning] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [currentStepText, setCurrentStepText] = useState('Bereit für Diagnose');
  const [percent, setPercent] = useState(0);
  
  // Terminal-Logs
  const [logs, setLogs] = useState([]);
  const [testResult, setTestResult] = useState(null); // 'success' | 'error' | null
  
  // Ref für das automatische Scrollen der Terminal-Box
  const terminalEndRef = useRef(null);

  // Automatisches Herunterscrollen bei neuen Log-Einträgen
  useEffect(() => {
    if (terminalEndRef.current) {
      terminalEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs]);

  // Startet den asynchronen Testlauf
  const handleStartTests = async () => {
    if (isRunning) return;
    
    setIsRunning(true);
    setTestResult(null);
    setCurrentStep(0);
    setPercent(0);
    setCurrentStepText('Initialisiere...');
    setLogs([]);

    try {
      await runApiTests(token, (progress) => {
        // Falls ein neuer Einzelschritt übermittelt wird, aktualisieren wir den Fortschritt
        if (progress.step !== undefined) {
          setCurrentStep(progress.step);
          setPercent(progress.percent || 0);
          setCurrentStepText(progress.stepText || '');
        }
        
        // Log-Meldung hinzufügen
        if (progress.message) {
          setLogs(prev => [...prev, {
            text: progress.message,
            type: progress.type || 'info',
            timestamp: new Date().toLocaleTimeString()
          }]);
        }
      });
      
      setTestResult('success');
      setCurrentStepText('Diagnose erfolgreich abgeschlossen');
      setPercent(100);
    } catch (err) {
      setTestResult('error');
      setCurrentStepText(`Diagnose abgebrochen: ${err.message}`);
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="vintage-settings py-4" style={{ maxWidth: '1000px', margin: '0 auto' }}>
      {/* Seiten-Header */}
      <h1 className="page-header">System-Diagnose</h1>
      <p className="text-muted mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
        Führen Sie eine automatisierte, interaktive End-to-End-Schnittstellenprüfung durch. 
        Dieses Tool verifiziert die Kommunikation mit allen Kernbereichen des Backends in Echtzeit, 
        indem es einen echten Benutzer-Workflow simuliert (Registrierung, Kalenderplanung, Buchung, 
        Zahlungsabwicklung, Bewertung, PDF-Erstellung und anschließendes spurloses Aufräumen).
      </p>

      {/* Haupt-Card */}
      <Card className="settings-card shadow-sm border-0 mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center bg-white py-3" style={{ borderBottom: '1px solid #dcd8d1' }}>
          <div>
            <h5 className="mb-0" style={{ fontFamily: "'Lora', serif", fontWeight: '700', color: '#3d3b38' }}>
              E2E Schnittstellen-Testsuite
            </h5>
            <small className="text-muted">Ausschließlich Lese- und Schreiboperationen über vorhandene Endpunkte</small>
          </div>
          
          <Button 
            className="form-button px-4 py-2"
            disabled={isRunning || !token}
            onClick={handleStartTests}
            style={{ 
              backgroundColor: isRunning ? '#8f887f' : '#b95c50', 
              borderColor: isRunning ? '#8f887f' : '#b95c50',
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: '700'
            }}
          >
            {isRunning ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Diagnose läuft...
              </>
            ) : (
              'Diagnose starten'
            )}
          </Button>
        </Card.Header>
        
        <Card.Body className="p-4" style={{ backgroundColor: '#fcfbf9' }}>
          {/* Fortschrittsanzeige */}
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="fw-bold" style={{ color: '#3d3b38', fontSize: '0.95rem' }}>
                {isRunning ? `Aktueller Schritt: ${currentStepText}` : currentStepText}
              </span>
              <span className="text-muted fw-bold">{percent}%</span>
            </div>
            
            <ProgressBar 
              now={percent} 
              variant="custom-orange" 
              className="vintage-progress"
              style={{ 
                height: '12px', 
                borderRadius: '6px', 
                backgroundColor: '#e9e5e0' 
              }}
            />
          </div>

          {/* Ergebnismeldung */}
          {testResult === 'success' && (
            <Alert variant="success" className="border-0 shadow-sm mb-4" style={{ borderRadius: '12px', backgroundColor: '#eefdf4', color: '#166534' }}>
              <h6 className="alert-heading fw-bold mb-1">✓ Diagnose bestanden</h6>
              <p className="mb-0 style-muted" style={{ fontSize: '0.88rem' }}>
                Alle Schnittstellentests wurden erfolgreich ausgeführt. Die PDF-Rechnung wurde korrekt generiert und alle angelegten Testentitäten wurden rückstandslos wieder bereinigt.
              </p>
            </Alert>
          )}

          {testResult === 'error' && (
            <Alert variant="danger" className="border-0 shadow-sm mb-4" style={{ borderRadius: '12px', backgroundColor: '#fef2f2', color: '#991b1b' }}>
              <h6 className="alert-heading fw-bold mb-1">✕ Diagnose fehlgeschlagen</h6>
              <p className="mb-0" style={{ fontSize: '0.88rem' }}>
                Bei der Schnittstellenprüfung ist ein Fehler aufgetreten. Bitte prüfen Sie die Terminal-Konsole unten auf Details.
              </p>
            </Alert>
          )}

          {/* Terminal Konsole */}
          <div className="terminal-header d-flex justify-content-between align-items-center px-3 py-2" style={{ backgroundColor: '#1d1b18', borderTopLeftRadius: '12px', borderTopRightRadius: '12px', borderBottom: '1px solid #3d352e' }}>
            <span style={{ fontFamily: 'Courier New, monospace', fontSize: '0.85rem', color: '#a89c90', fontWeight: 'bold' }}>
              SYSTEM DIAGNOSTIC CONSOLE
            </span>
            <div className="d-flex gap-1.5">
              <span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#ef4444', display: 'inline-block' }}></span>
              <span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#eab308', display: 'inline-block', marginLeft: '4px' }}></span>
              <span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#22c55e', display: 'inline-block', marginLeft: '4px' }}></span>
            </div>
          </div>

          <div 
            className="terminal-body p-3" 
            style={{ 
              backgroundColor: '#151311', 
              borderBottomLeftRadius: '12px', 
              borderBottomRightRadius: '12px', 
              height: '350px', 
              overflowY: 'auto',
              boxShadow: 'inset 0 4px 10px rgba(0,0,0,0.5)',
              fontFamily: 'Courier New, Courier, monospace',
              fontSize: '0.9rem',
              lineHeight: '1.4'
            }}
          >
            {logs.length === 0 ? (
              <div className="text-center text-muted py-5" style={{ color: '#665e56' }}>
                &gt;_ Konsole bereit. Klicken Sie auf "Diagnose starten", um den Durchlauf zu beginnen.
              </div>
            ) : (
              logs.map((logItem, index) => {
                let logColor = '#e8d8c8'; // Default warm white
                let prefix = '';
                
                if (logItem.type === 'success') {
                  logColor = '#22c55e'; // Green
                  prefix = '[ OK ] ';
                } else if (logItem.type === 'error') {
                  logColor = '#ef4444'; // Red
                  prefix = '[FAIL] ';
                } else if (logItem.type === 'header') {
                  logColor = '#f59e0b'; // Amber/Orange
                } else {
                  prefix = '[INFO] ';
                }

                return (
                  <div key={index} style={{ color: logColor, marginBottom: '4px', whiteSpace: 'pre-wrap' }}>
                    <span style={{ color: '#5c544d', marginRight: '8px' }}>[{logItem.timestamp}]</span>
                    <span>{prefix}{logItem.text}</span>
                  </div>
                );
              })
            )}
            <div ref={terminalEndRef} />
          </div>
        </Card.Body>
      </Card>
      
      {/* Fortschrittsbalken-Farbanpassung im Stylesheet */}
      <style>{`
        .progress-bar.bg-custom-orange {
          background-color: #b95c50 !important;
        }
        .vintage-progress {
          box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
        }
      `}</style>
    </div>
  );
}
